import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HoverColor, IconColor, IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { TooltipComponent } from '@freelancer/ui/tooltip';

export enum SocialButtonColor {
  LIGHT = 'light',
  DARK = 'dark',
  COLORED = 'colored',
}

export enum SocialButtonType {
  ICON = 'icon',
  BUTTON = 'button',
}

export enum SocialButtonSize {
  SMALL = IconSize.SMALL,
  MID = IconSize.MID,
}

@Component({
  selector: 'fl-social-buttons',
  template: `
    <fl-bit
      class="SocialButtonItem"
      role="listitem"
      [flMarginRight]="Margin.XXXSMALL"
    >
      <fl-button
        class="SocialButton SocialButton--facebook"
        [attr.data-type]="type"
        [attr.data-theme]="color"
        [link]="shareUrl ? shareUrls['facebook'] : socialUrls['facebook']"
        [newTab]="true"
        [color]="shareUrl ? ButtonColor.CUSTOM : null"
        [size]="shareUrl ? ButtonSize.MINI : null"
        (click)="handleClick('facebook')"
      >
        <fl-icon
          *ngIf="shareUrl"
          aria-label="Share on Facebook"
          i18n-aria-label="Share link on Facebook social button"
          [color]="IconColor.INHERIT"
          [hoverColor]="HoverColor.CURRENT"
          [name]="'ui-facebook'"
          [size]="size"
        ></fl-icon>
        <fl-icon
          *ngIf="!shareUrl"
          aria-label="Facebook"
          i18n-aria-label="Facebook social button"
          [color]="IconColor.INHERIT"
          [hoverColor]="HoverColor.CURRENT"
          [name]="'ui-facebook'"
          [size]="size"
        ></fl-icon>
      </fl-button>
    </fl-bit>
    <fl-bit
      class="SocialButtonItem"
      role="listitem"
      [flMarginRight]="Margin.XXXSMALL"
    >
      <fl-button
        class="SocialButton SocialButton--twitter"
        [attr.data-type]="type"
        [attr.data-theme]="color"
        [link]="shareUrl ? shareUrls['twitter'] : socialUrls['twitter']"
        [newTab]="true"
        [color]="shareUrl ? ButtonColor.CUSTOM : null"
        [size]="shareUrl ? ButtonSize.MINI : null"
        (click)="handleClick('twitter')"
      >
        <fl-icon
          *ngIf="shareUrl"
          aria-label="Share on Twitter"
          i18n-aria-label="Share link on Twitter social button"
          [color]="IconColor.INHERIT"
          [hoverColor]="HoverColor.CURRENT"
          [name]="'ui-twitter'"
          [size]="size"
        ></fl-icon>
        <fl-icon
          *ngIf="!shareUrl"
          aria-label="Twitter"
          i18n-aria-label="Facebook social button"
          [color]="IconColor.INHERIT"
          [hoverColor]="HoverColor.CURRENT"
          [name]="'ui-twitter'"
          [size]="size"
        ></fl-icon>
      </fl-button>
    </fl-bit>
    <fl-bit
      class="SocialButtonItem"
      role="listitem"
      *ngIf="!shareUrl"
      [flMarginRight]="Margin.XXXSMALL"
    >
      <fl-button
        class="SocialButton SocialButton--youtube"
        [attr.data-type]="type"
        [attr.data-theme]="color"
        [link]="socialUrls['youtube']"
        [newTab]="true"
        (click)="handleClick('youtube')"
      >
        <fl-icon
          aria-label="YouTube"
          i18n-aria-label="YouTube social button"
          [color]="IconColor.INHERIT"
          [hoverColor]="HoverColor.CURRENT"
          [name]="'ui-youtube'"
          [size]="size"
        ></fl-icon>
      </fl-button>
    </fl-bit>
    <fl-bit
      class="SocialButtonItem"
      role="listitem"
      *ngIf="!shareUrl"
      [flMarginRight]="Margin.XXXSMALL"
    >
      <fl-button
        class="SocialButton SocialButton--instagram"
        [attr.data-type]="type"
        [attr.data-theme]="color"
        [link]="socialUrls['instagram']"
        [newTab]="true"
        (click)="handleClick('instagram')"
      >
        <fl-icon
          aria-label="Instagram"
          i18n-aria-label="Instagram social button"
          [color]="IconColor.INHERIT"
          [hoverColor]="HoverColor.CURRENT"
          [name]="'ui-instagram'"
          [size]="size"
        ></fl-icon>
      </fl-button>
    </fl-bit>
    <fl-bit
      class="SocialButtonItem"
      role="listitem"
      *ngIf="shareUrl"
      [flMarginRight]="Margin.XXXSMALL"
    >
      <fl-button
        class="SocialButton SocialButton--linkedin"
        [attr.data-type]="type"
        [attr.data-theme]="color"
        [link]="shareUrls['linkedin']"
        [newTab]="true"
        [color]="ButtonColor.CUSTOM"
        [size]="ButtonSize.MINI"
        (click)="handleClick('linkedin')"
      >
        <fl-icon
          aria-label="Share on LinkedIn"
          i18n-aria-label="Share link on LinkedIn social button"
          [color]="IconColor.INHERIT"
          [hoverColor]="HoverColor.CURRENT"
          [name]="'ui-linkedin'"
          [size]="size"
        ></fl-icon>
      </fl-button>
    </fl-bit>
    <fl-bit
      class="SocialButtonItem"
      role="listitem"
      *ngIf="shareUrl"
      [flMarginRight]="Margin.XXXSMALL"
    >
      <fl-button
        class="SocialButton SocialButton--pinterest"
        [attr.data-type]="type"
        [attr.data-theme]="color"
        [link]="shareUrls['pinterest']"
        [newTab]="true"
        [color]="ButtonColor.CUSTOM"
        [size]="ButtonSize.MINI"
        (click)="handleClick('pinterest')"
      >
        <fl-icon
          aria-label="Share on Pinterest"
          i18n-aria-label="Share link on Pinterest social button"
          [color]="IconColor.INHERIT"
          [hoverColor]="HoverColor.CURRENT"
          [name]="'ui-pinterest'"
          [size]="size"
        ></fl-icon>
      </fl-button>
    </fl-bit>
    <fl-bit
      class="SocialButtonItem"
      role="listitem"
      *ngIf="shareUrl"
      [flMarginRight]="Margin.XXXSMALL"
    >
      <fl-tooltip
        *ngIf="copySuccessful"
        i18n-message="Copy to clipboard tooltip message"
        message="Copied!"
        (mouseleave)="resetCopyCliboard()"
      >
        <ng-container *ngTemplateOutlet="copyLinkButton"></ng-container>
      </fl-tooltip>

      <fl-tooltip
        *ngIf="!copySuccessful"
        i18n-message="Copy to clipboard tooltip message"
        message="Copy Link"
        (mouseleave)="resetCopyCliboard()"
      >
        <ng-container *ngTemplateOutlet="copyLinkButton"></ng-container>
      </fl-tooltip>

      <ng-template #copyLinkButton>
        <fl-button
          class="SocialButton SocialButton--copyLink"
          [attr.data-type]="type"
          [attr.data-theme]="color"
          [color]="ButtonColor.CUSTOM"
          [size]="ButtonSize.MINI"
          (click)="copyToClipboard(shareUrl || '')"
        >
          <fl-icon
            aria-label="Copy link to clipboard"
            i18n-aria-label="Copy link to clipboard social button"
            [color]="IconColor.INHERIT"
            [hoverColor]="HoverColor.CURRENT"
            [name]="'ui-link'"
            [size]="size"
          ></fl-icon>
        </fl-button>
      </ng-template>
    </fl-bit>
    <fl-text
      class="ClipboardError"
      i18n="Copy to clipboard error message"
      *ngIf="copyFailed"
      [color]="FontColor.ERROR"
      [size]="TextSize.XXSMALL"
    >
      Copy to clipboard failed, please refresh this page to adjust your
      permission.
    </fl-text>
  `,
  styleUrls: ['./social-buttons.component.scss'],
})
export class SocialButtonsComponent implements OnChanges, OnInit {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  TextSize = TextSize;
  IconColor = IconColor;
  HoverColor = HoverColor;
  Margin = Margin;

  copySuccessful = false;
  copyFailed = false;
  isCopyAllowed = false;

  @Input() color: SocialButtonColor = SocialButtonColor.DARK;
  @Input() shareUrl?: string;
  @Input() shareText?: string;
  @Input() size = SocialButtonSize.MID;

  /** Maximize the width of the parent container */
  @HostBinding('attr.data-fluid')
  @Input()
  fluid?: boolean;

  @HostBinding('attr.data-type')
  type: SocialButtonType;

  @HostBinding('attr.role')
  role = 'list';

  @ViewChild(TooltipComponent)
  tooltipComponent: TooltipComponent;

  shareUrls: { [key: string]: string } = {};
  socialUrls: { [key: string]: string } = {
    facebook: 'https://facebook.com/fansoffreelancer',
    twitter: 'https://twitter.com/freelancer',
    youtube: 'https://youtube.com/freelancerchannel',
    instagram: 'https://instagram.com/freelancerofficial',
  };

  constructor(
    private renderer: Renderer2,
    private timeUtils: TimeUtils,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnChanges() {
    this.setType();
    this.createShareUrls();
  }

  ngOnInit() {
    this.setType();
  }

  setType() {
    this.type = this.shareUrl ? SocialButtonType.BUTTON : SocialButtonType.ICON;
  }

  createShareUrls() {
    const text = (this.shareText || '').split(' ').join('+');

    if (!this.shareUrl) {
      return;
    }

    this.shareUrls.facebook = `http://www.facebook.com/share.php?u=${this.shareUrl}`;
    this.shareUrls.twitter = `https://twitter.com/intent/tweet?text=${text}: ${this.shareUrl} via @freelancer Community`;
    this.shareUrls.linkedin = `http://www.linkedin.com/shareArticle?mini=true&url=${this.shareUrl}&title=${text}&source=https://www.freelancer.com/community`;
    this.shareUrls.pinterest = `https://pinterest.com/pin/create/bookmarklet/?url=${this.shareUrl}`;
    this.shareUrls.copyLink = this.shareUrl;
  }

  copyToClipboard(url: string) {
    if (!navigator.clipboard) {
      this.copyToClipboardFallback(url);
      return;
    }

    navigator.clipboard.writeText(url).then(
      () => {
        this.toggleCopyClipboardState(true);
      },
      () => {
        this.toggleCopyClipboardState(false);
      },
    );
  }

  /**
   * Fallback for browers that doesn't support navigator.clipboard.
   *
   * For IE11, a permission request shows up and it is not possible to check
   * whether user did not allow clipboard access since document.execCommand('copy')
   * always returns true regardless of what the user picks, therefore we need
   * to manually check if copy to clipboard was sucessful by creating 2 textareas.
   *
   * 1. input = holds the string to be copied
   * 2. input2 = paste the string here from the clipboard
   *
   * If both input's values doesn't match, it'll assume that copyToClipboard
   * wasn't successful.
   */
  copyToClipboardFallback(url: string) {
    if (
      document.queryCommandSupported &&
      document.queryCommandSupported('copy')
    ) {
      const input = this.renderer.createElement('input') as HTMLInputElement;
      const input2 = this.renderer.createElement('input') as HTMLInputElement;

      input.value = url;
      this.renderer.setStyle(input, 'position', 'fixed');
      this.renderer.setStyle(input, 'opacity', '0');
      this.renderer.setStyle(input2, 'position', 'fixed');
      this.renderer.setStyle(input2, 'opacity', '0');
      this.renderer.appendChild(document.body, input);
      this.renderer.appendChild(document.body, input2);

      // Execute copy on the first input
      input.select();
      document.execCommand('copy');

      // Execute paste on the second input
      input2.select();
      const canPaste = document.execCommand('paste');

      // Check if paste command is executed which is only permitted in IE11
      if (canPaste) {
        if (input.value === input2.value) {
          this.toggleCopyClipboardState(true);
        } else {
          this.copySuccessful = false;
          this.copyFailed = true;
        }

        // Manually hides the tooltip as mouseleave to hide the tooltip doesn't
        // trigger when a permission prompt shows up for the first time
        if (!this.isCopyAllowed && !this.copyFailed) {
          this.isCopyAllowed = true;
          this.toggleCopyClipboardState(true);
          this.timeUtils.setTimeout(() => {
            this.resetCopyCliboard();
          }, 1000);
        }
      } else {
        this.toggleCopyClipboardState(true);
      }
      this.renderer.removeChild(document.body, input);
      this.renderer.removeChild(document.body, input2);
    } else {
      this.copyFailed = true;
    }
  }

  toggleCopyClipboardState(state: boolean) {
    if (state) {
      this.copyFailed = false;
      this.copySuccessful = true;
    } else {
      this.copySuccessful = false;
      this.copyFailed = true;
    }

    this.changeDetectorRef.detectChanges();

    // Re-align tooltip by redrawing it
    this.tooltipComponent.hide();
    this.tooltipComponent.show();
  }

  resetCopyCliboard() {
    this.copySuccessful = false;
    this.changeDetectorRef.detectChanges();
    this.tooltipComponent.hide();
  }

  // Tracking could be added here later if required.
  handleClick(platform: string) {
    // Empty
  }
}
